import React from 'react';
import { Dropdown } from 'primereact/dropdown';
import { useEsMovil } from '../../../indicadores/hooks/useEsMovil';
import type { EstadoTienda } from '../../types/metas.types';
import { etiquetaEstado } from '../../utils/estado';
import { CLASE_BOTON_ICONO, CLASE_OVERLAY } from './clases';
import { EstadoPill } from './Pill';
import { Field } from './Field';

export interface OpcionTienda {
  /** Llave estable (la Captura usa `CADENA|NUM`). */
  valor: string;
  /** «VAZ 1 · REGINA». */
  etiqueta: string;
  /** Si viene, la lista lo pinta como `EstadoPill` junto al nombre. */
  estado?: EstadoTienda | string;
  /**
   * Otro nombre para el estado, con el mismo tono («Te la envió el gerente»,
   * «Devuelta al gerente»: la Captura del supervisor, 21-oct-2026).
   */
  textoEstado?: string;
}

interface Props {
  opciones: OpcionTienda[];
  value: string | null;
  /**
   * Se llama con la tienda elegida (lista, flecha anterior o siguiente). Quien
   * lo usa decide si pregunta antes por cambios sin guardar.
   */
  onChange: (valor: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  /** `id` del control (para su `<label>`). Por omisión `metas-tienda`. */
  id?: string;
  etiqueta?: string;
  /** Búsqueda dentro de la lista (por omisión, sí). */
  filtro?: boolean;
  className?: string;
}

/** La tienda anterior y la siguiente de la lista (sin dar la vuelta). Sin tienda elegida, «siguiente» es la primera. */
export function vecinasDeTienda(
  opciones: { valor: string }[],
  value: string | null,
): { anterior: string | null; siguiente: string | null } {
  if (!opciones.length) return { anterior: null, siguiente: null };
  const i = value === null ? -1 : opciones.findIndex((o) => o.valor === value);
  if (i < 0) return { anterior: null, siguiente: opciones[0].valor };
  return {
    anterior: i > 0 ? opciones[i - 1].valor : null,
    siguiente: i < opciones.length - 1 ? opciones[i + 1].valor : null,
  };
}

const Flecha: React.FC<{ dir: 'izq' | 'der' }> = ({ dir }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={dir === 'izq' ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} />
  </svg>
);

/**
 * Selector de tienda con anterior / siguiente, como en el diseño: la lista
 * (con buscador y la pastilla de estado de cada tienda) y dos flechas para
 * recorrerlas sin abrirla.
 */
export const SelectorTienda: React.FC<Props> = ({
  opciones,
  value,
  onChange,
  disabled,
  placeholder = 'Elige una tienda',
  emptyMessage = 'No hay tiendas',
  id = 'metas-tienda',
  etiqueta = 'Tienda',
  filtro = true,
  className,
}) => {
  const { anterior, siguiente } = vecinasDeTienda(opciones, value);
  // En el teléfono sólo el nombre («VAZ 1 · REGINA»): el estado ya está en la
  // tarjeta resumen, y con él no cabía («VAZ 1 · REGINA — Sin c»).
  const esMovil = useEsMovil(767);
  return (
    <Field etiqueta={etiqueta} htmlFor={id} className={className}>
      <div className="flex min-w-0 items-center gap-2">
        <Dropdown
          inputId={id}
          value={value}
          options={opciones}
          optionLabel="etiqueta"
          optionValue="valor"
          onChange={(e) => onChange(e.value ?? null)}
          disabled={disabled}
          filter={filtro}
          placeholder={placeholder}
          emptyMessage={emptyMessage}
          emptyFilterMessage="Ninguna tienda coincide"
          panelClassName={CLASE_OVERLAY}
          // 200 px por omisión: en el teléfono se veían 4 tiendas (auditoría del 22-oct-2026).
          scrollHeight="320px"
          className="min-w-0 flex-grow md:max-w-[420px]"
          // Lo elegido se lee como en el diseño: «VAZ 1 · REGINA — Borrador».
          valueTemplate={(o: OpcionTienda | null) => (
            <span className="block min-w-0 truncate">
              {o ? `${o.etiqueta}${o.estado && !esMovil ? ` — ${o.textoEstado ?? etiquetaEstado(o.estado)}` : ''}` : placeholder}
            </span>
          )}
          itemTemplate={(o: OpcionTienda) => (
            <div className="flex w-full items-center justify-between gap-2">
              <span>{o.etiqueta}</span>
              {o.estado && <EstadoPill estado={o.estado} texto={o.textoEstado} />}
            </div>
          )}
        />
        <button
          type="button"
          className={CLASE_BOTON_ICONO}
          aria-label="Tienda anterior"
          disabled={disabled || !anterior}
          onClick={() => anterior && onChange(anterior)}
        >
          <Flecha dir="izq" />
        </button>
        <button
          type="button"
          className={CLASE_BOTON_ICONO}
          aria-label="Tienda siguiente"
          disabled={disabled || !siguiente}
          onClick={() => siguiente && onChange(siguiente)}
        >
          <Flecha dir="der" />
        </button>
      </div>
    </Field>
  );
};

export default SelectorTienda;
